import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { Options } from 'ngx-slider-v2';
import { OkAloneService } from 'src/app/ok-alone.service';
import {
  circlePolygonFromPoint,
  geometryShapeMode,
  getPointCoordinates,
  getPolygonRingCoordinates,
  normalizeThreatGeometry,
  ThreatShapeMode,
} from '../threat-geometry.util';
import * as _ from 'lodash-es';

declare var mapboxgl: any;
declare var MapboxDraw: any;

@Component({
  selector: 'app-threats-edit',
  templateUrl: './threats-edit.component.html',
  styleUrls: ['./threats-edit.component.scss']
})
export class ThreatsEditComponent implements OnInit, OnDestroy {

  id?: number;
  threat: any;
  meta: any = { severities: [], shapes: ['polygon', 'pin'], radius: { default: 1, min: 0.2, max: 20 } };
  formGroup!: FormGroup;
  submitted = false;
  map: any;
  draw: any;
  geometry: any = null;
  shapeMode: ThreatShapeMode = 'polygon';
  radiusMiles = 0.1;
  radiusSliderOptions: Options = {
    floor: 0.1,
    ceil: 10,
    step: 0.1,
    animate: true,
    hidePointerLabels: true,
    hideLimitLabels: true,
  };

  private readonly mapboxToken = 'pk.eyJ1IjoidHJ1c3R5b3giLCJhIjoiY2wxY2Y0emQ1MDcwOTNwcDNxYTd3ODB1ZyJ9.moSeRr49cY5d14hosZeT1w';
  private readonly pinCircleSourceId = 'threat-pin-radius';
  private readonly pinCircleFillLayerId = 'threat-pin-radius-fill';
  private readonly pinCircleLineLayerId = 'threat-pin-radius-line';

  get f(): { [key: string]: AbstractControl } {
    return this.formGroup.controls;
  }

  get isPin(): boolean {
    return this.shapeMode === 'pin';
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    public okService: OkAloneService,
    public transloco: TranslocoService
  ) { }

  ngOnInit(): void {
    this.id = +this.route.snapshot.params['id'];
    this.okService.get('threat-management/meta', {}).subscribe((data) => {
      this.meta = data.body ?? this.meta;
    });
    this.getThreat();
  }

  ngOnDestroy(): void {
    if (this.map) {
      this.map.remove();
    }
  }

  getThreat(): void {
    this.okService.get('threat-management/' + this.id, {}).subscribe((data) => {
      this.threat = data.body;
      this.setForm();
      setTimeout(() => this.setupMap(), 0);
    });
  }

  setForm(): void {
    this.formGroup = new FormGroup({
      severity: new FormControl(this.threat.severity, [Validators.required]),
      headline: new FormControl(this.threat.headline ?? this.threat.event ?? '', [Validators.required]),
      description: new FormControl(this.threat.description ?? '', [Validators.required]),
      area_desc: new FormControl(this.threat.area_desc ?? '', [Validators.required]),
      effective: new FormControl(this.toDateInput(this.threat.effective)),
      expires: new FormControl(this.toDateInput(this.threat.expires)),
    });
    this.geometry = normalizeThreatGeometry(this.threat.geometry ?? null);
    this.shapeMode = geometryShapeMode(this.geometry);
    const rawRadius = this.threat.radius_miles ?? this.meta?.radius?.default ?? 1;
    const sliderMin = this.radiusSliderOptions.floor ?? 0.2;
    const sliderMax = this.radiusSliderOptions.ceil ?? 20;
    this.radiusMiles = Math.min(sliderMax, Math.max(sliderMin, Math.round(rawRadius * 10) / 10));
  }

  onShapeModeChange(mode: ThreatShapeMode): void {
    if (this.shapeMode === mode) {
      return;
    }

    this.shapeMode = mode;
    this.geometry = null;
    if (this.draw) {
      this.draw.deleteAll();
    }
    this.clearPinRadiusLayers();
    this.configureDrawControls();
  }

  onRadiusChange(): void {
    this.syncPinRadiusLayer();
  }

  toDateInput(value: string | null): string {
    if (!value) {
      return '';
    }

    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.substring(0, 10);
    }

    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return '';
    }

    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  setupMap(): void {
    if (typeof mapboxgl === 'undefined' || typeof MapboxDraw === 'undefined') {
      return;
    }

    mapboxgl.accessToken = this.mapboxToken;
    this.map = new mapboxgl.Map({
      container: 'threat-draw-map',
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [this.threat?.centroid_lng ?? -98.5795, this.threat?.centroid_lat ?? 39.8283],
      zoom: 8,
    });

    this.map.addControl(new mapboxgl.NavigationControl());

    this.draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: {
        polygon: true,
        point: true,
        trash: true,
      },
    });

    this.map.addControl(this.draw);

    this.map.on('load', () => {
      if (this.geometry) {
        this.loadGeometryIntoDraw(this.geometry);
      }
      this.configureDrawControls();
    });

    this.map.on('draw.create', (e: any) => this.syncFromMap(e));
    this.map.on('draw.update', (e: any) => this.syncFromMap(e));
    this.map.on('draw.delete', () => {
      this.geometry = null;
      this.clearPinRadiusLayers();
    });
  }

  configureDrawControls(): void {
    if (!this.draw) {
      return;
    }

    if (this.shapeMode === 'pin') {
      this.draw.changeMode('simple_select');
    } else {
      this.draw.changeMode('simple_select');
    }
  }

  loadGeometryIntoDraw(geometry: any): void {
    if (!this.draw || !geometry) {
      return;
    }

    const normalized = normalizeThreatGeometry(geometry);

    this.draw.deleteAll();
    this.draw.add({
      type: 'Feature',
      geometry: normalized,
    });

    this.syncGeometryFromDraw();

    if (this.shapeMode === 'pin') {
      this.syncPinRadiusLayer();
      const point = getPointCoordinates(normalized);
      if (point && this.map) {
        this.map.flyTo({ center: point, zoom: 12 });
      }
      return;
    }

    const coords = getPolygonRingCoordinates(normalized);

    if (coords && coords.length > 0 && this.map) {
      const bounds = coords.reduce(
        (b: any, c: any) => b.extend(c),
        new mapboxgl.LngLatBounds(coords[0], coords[0])
      );
      this.map.fitBounds(bounds, { padding: 60 });
    }
  }

  syncGeometryFromDraw(): void {
    if (!this.draw) {
      return;
    }

    const data = this.draw.getAll();
    if (data?.features?.length > 0) {
      this.geometry = normalizeThreatGeometry(data.features[0].geometry);
      this.syncPinRadiusLayer();
    }
  }

  syncFromMap(e: any): void {
    if (!e.features || e.features.length === 0) {
      return;
    }

    const feature = e.features[0];
    const geometry = normalizeThreatGeometry(feature.geometry);

    if (this.shapeMode === 'pin') {
      if (geometry?.type !== 'Point') {
        return;
      }
      if (e.type === 'draw.create' && this.draw) {
        const all = this.draw.getAll();
        if (all?.features?.length > 1) {
          const keepId = feature.id;
          all.features.forEach((f: any) => {
            if (f.id !== keepId) {
              this.draw.delete(f.id);
            }
          });
        }
      }
    } else if (geometry?.type !== 'Polygon' && geometry?.type !== 'MultiPolygon') {
      return;
    }

    this.geometry = geometry;
    this.syncPinRadiusLayer();
  }

  syncPinRadiusLayer(): void {
    if (!this.map || this.shapeMode !== 'pin') {
      this.clearPinRadiusLayers();
      return;
    }

    const point = getPointCoordinates(this.geometry);
    if (!point || !this.radiusMiles || this.radiusMiles <= 0) {
      this.clearPinRadiusLayers();
      return;
    }

    const circle = circlePolygonFromPoint(point[0], point[1], this.radiusMiles);
    const addLayers = () => {
      if (!this.map.getSource(this.pinCircleSourceId)) {
        this.map.addSource(this.pinCircleSourceId, {
          type: 'geojson',
          data: { type: 'Feature', geometry: circle },
        });
        this.map.addLayer({
          id: this.pinCircleFillLayerId,
          type: 'fill',
          source: this.pinCircleSourceId,
          paint: { 'fill-color': '#ef4444', 'fill-opacity': 0.15 },
        });
        this.map.addLayer({
          id: this.pinCircleLineLayerId,
          type: 'line',
          source: this.pinCircleSourceId,
          paint: { 'line-color': '#ef4444', 'line-width': 2 },
        });
      } else {
        this.map.getSource(this.pinCircleSourceId).setData({ type: 'Feature', geometry: circle });
      }
    };

    if (this.map.isStyleLoaded()) {
      addLayers();
    } else {
      this.map.once('load', addLayers);
    }
  }

  clearPinRadiusLayers(): void {
    if (!this.map) {
      return;
    }

    [this.pinCircleLineLayerId, this.pinCircleFillLayerId].forEach((layerId) => {
      if (this.map.getLayer(layerId)) {
        this.map.removeLayer(layerId);
      }
    });
    if (this.map.getSource(this.pinCircleSourceId)) {
      this.map.removeSource(this.pinCircleSourceId);
    }
  }

  buildPayload(): any {
    const values = this.formGroup.value;
    const payload: any = {
      category: 'custom',
      event: values.headline,
      severity: values.severity,
      headline: values.headline,
      area_desc: values.area_desc,
      description: values.description,
      effective: values.effective || null,
      expires: values.expires || null,
      geometry: normalizeThreatGeometry(this.geometry),
    };

    if (this.shapeMode === 'pin') {
      payload.radius_miles = this.radiusMiles;
    }

    return payload;
  }

  private apiErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'string') {
      return error;
    }
    if (error && typeof error === 'object') {
      const payload = error as { message?: string; error?: string };
      if (typeof payload.message === 'string' && payload.message.length > 0) {
        return payload.message;
      }
      if (typeof payload.error === 'string' && payload.error.length > 0) {
        return payload.error;
      }
    }

    return this.transloco.translate('We couldn’t process your request just now. Please check your details and try again.');
  }

  submitForm(): void {
    if (this.submitted) {
      Object.keys(this.formGroup.controls).forEach(key => {
        this.formGroup.get(key)?.setErrors(null);
        this.formGroup.get(key)?.updateValueAndValidity();
      });
    }

    this.submitted = true;
    this.syncGeometryFromDraw();

    if (!this.geometry) {
      const message = this.shapeMode === 'pin'
        ? this.transloco.translate('Please place a pin on the map.')
        : this.transloco.translate('Please draw a boundary on the map.');
      this.okService.setAlert(message, 'warning');
      return;
    }

    if (this.shapeMode === 'pin' && (!this.radiusMiles || this.radiusMiles <= 0)) {
      this.okService.setAlert(this.transloco.translate('Please enter a valid radius for the pin.'), 'warning');
      return;
    }

    if (this.formGroup.valid) {
      this.okService.put('threat-management/' + this.id, this.buildPayload()).subscribe({
        next: () => {
          this.okService.setAlert(this.transloco.translate('The threat has been updated'), 'success');
          this.router.navigateByUrl('/mgmt/threats');
        },
        error: (e) => {
          if (Array.isArray(e)) {
            _.forEach(e, (error: any) => {
              this.formGroup.get(error.field)?.setErrors({ invalid: error.message });
            });
          } else {
            this.okService.setAlert(this.apiErrorMessage(e), 'warning');
          }
        }
      });
    }
  }

  doDeactivate(): void {
    if (!this.threat || !confirm(this.transloco.translate('Are you sure you want to deactivate') + ' ' + (this.threat.headline ?? this.threat.event) + '?')) {
      return;
    }

    this.okService.delete('threat-management/' + this.id).subscribe({
      next: () => {
        this.okService.setAlert(this.transloco.translate('The threat has been deactivated'), 'success');
        this.router.navigateByUrl('/mgmt/threats');
      },
      error: (e) => {
        this.okService.setAlert(this.apiErrorMessage(e), 'warning');
      }
    });
  }
}
